import React, { useEffect, useRef, useState } from 'react';
import VoidRefundPanel from '../components/VoidRefundPanel';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import { adminApi } from '../services/api';

const DEFAULT_FILTERS = { minAgeDays: '7', lookbackDays: '90', search: '', carrier: '', service: '' };
const FIELD_CLASS = 'mt-1 w-full min-h-[44px] rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:ring-indigo-500';
const BUTTON_CLASS = 'min-h-[44px] rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2';
const CLASSIFICATIONS = {
  LIKELY_UNUSED: { label: 'Likely unused', className: 'bg-amber-100 text-amber-900' },
  TRACKING_UNKNOWN: { label: 'Tracking unknown', className: 'bg-gray-100 text-gray-800' },
  CHECK_FAILED: { label: 'Check failed', className: 'bg-red-100 text-red-800' },
  USED: { label: 'Carrier activity reported', className: 'bg-blue-100 text-blue-800' },
  EXCLUDED: { label: 'Excluded', className: 'bg-gray-100 text-gray-700' },
};

const formatDate = (value) => {
  if (!value) return 'Not available';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 'Not available' : date.toLocaleString();
};

const formatAmount = (amount, currency) => {
  if (amount == null || amount === '' || !Number.isFinite(Number(amount))) return 'Not available';
  const code = String(currency || '').toUpperCase();
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency', currency: code, currencyDisplay: 'code',
    }).format(Number(amount));
  } catch {
    return `${Number(amount).toFixed(2)} ${code || '(currency unknown)'}`;
  }
};

const trackingLink = (value) => {
  if (typeof value !== 'string') return null;
  try {
    const url = new URL(value);
    return ['http:', 'https:'].includes(url.protocol) ? url.href : null;
  } catch {
    return null;
  }
};

const Detail = ({ title, children }) => (
  <div className="min-w-0">
    <dt className="text-xs font-medium text-gray-500">{title}</dt>
    <dd className="mt-1 break-words text-sm text-gray-900">{children}</dd>
  </div>
);

const LabelCard = ({ item, selectable = false, selected = false, onToggle }) => {
  const classification = CLASSIFICATIONS[item.classification] || CLASSIFICATIONS.TRACKING_UNKNOWN;
  const href = trackingLink(item.trackingUrl);
  return (
    <li className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <h3 className="break-all text-base font-semibold text-gray-900">Order {item.orderId || item.id}</h3>
        <span className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${classification.className}`}>
          {classification.label}
        </span>
      </div>
      {selectable && (
        <label className="mt-2 flex items-center gap-2 text-sm text-gray-800">
          <input
            type="checkbox"
            checked={selected}
            onChange={() => onToggle(item.orderId)}
            className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
          />
          <span>Select for void &amp; refund<span className="sr-only"> — order {item.orderId}</span></span>
        </label>
      )}
      <p className="mt-2 text-sm text-gray-700">{item.reason || 'Review the current label and tracking status.'}</p>
      <dl className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Detail title="Customer">
          {item.customerName && <span className="block">{item.customerName}</span>}
          <span className="break-all">{item.email || 'Email not available'}</span>
        </Detail>
        <Detail title="Carrier / service">
          {item.carrier || 'Carrier unknown'}
          {item.serviceName && <span className="block text-gray-600">{item.serviceName}</span>}
        </Detail>
        <Detail title="Purchased / age">
          <span className="block">{formatDate(item.createdAt)}</span>
          <span className="text-gray-600">{item.ageDays == null ? 'Age not available' : `${item.ageDays} days old`}</span>
        </Detail>
        <Detail title="Amount paid">{formatAmount(item.amountPaid, item.currency)}</Detail>
      </dl>
      <details className="mt-4 border-t border-gray-100 pt-3">
        <summary className="min-h-[36px] cursor-pointer text-sm font-medium text-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500">
          Review tracking details<span className="sr-only"> for order {item.orderId || item.id}</span>
        </summary>
        <dl className="mt-2 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <Detail title="Tracking number">
            {href ? (
              <a href={href} target="_blank" rel="noopener noreferrer" className="break-all text-indigo-700 underline hover:text-indigo-900">
                {item.trackingNumber || 'Open carrier tracking'}<span className="sr-only"> (opens in a new tab)</span>
              </a>
            ) : (item.trackingNumber || 'Not available')}
          </Detail>
          <Detail title="Carrier tracking status">{item.trackingStatus || 'Unknown'}</Detail>
          <Detail title="Label / payment status">
            {item.transactionStatus || 'Unknown'} / {item.paymentStatus || 'Unknown'}
          </Detail>
          <Detail title="Last checked">{formatDate(item.checkedAt)}</Detail>
        </dl>
      </details>
    </li>
  );
};

const LabelGroup = ({ title, description, items, emptyMessage, selectable = false, selectedIds = [], onToggle }) => (
  <section className="mt-6">
    <h2 className="text-lg font-semibold text-gray-900">{title} ({items.length})</h2>
    <p className="mt-1 text-sm text-gray-600">{description}</p>
    {items.length ? (
      <ul className="mt-3 space-y-3">{items.map((item) => (
        <LabelCard
          key={item.id || item.orderId}
          item={item}
          selectable={selectable}
          selected={selectedIds.includes(item.orderId)}
          onToggle={onToggle}
        />
      ))}</ul>
    ) : <p className="mt-3 rounded-md border border-gray-200 bg-white p-4 text-sm text-gray-600">{emptyMessage}</p>}
  </section>
);

const UnusedLabels = () => {
  const [draft, setDraft] = useState(DEFAULT_FILTERS);
  const [query, setQuery] = useState({ ...DEFAULT_FILTERS, page: 1, pageSize: 25 });
  const [filtersChanged, setFiltersChanged] = useState(false);
  const [validationError, setValidationError] = useState('');
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  // Selection is only offered for LIKELY_UNUSED rows — the group where a void has a
  // defensible basis. 'Needs review' rows have unconfirmed usage and are deliberately
  // not selectable.
  const [selectedIds, setSelectedIds] = useState([]);
  const toggleSelected = (orderId) =>
    setSelectedIds((prev) =>
      prev.includes(orderId) ? prev.filter((id) => id !== orderId) : [...prev, orderId]
    );
  const requestId = useRef(0);

  useEffect(() => {
    const currentRequest = ++requestId.current;
    setData(null);
    setError('');
    setLoading(true);
    setFiltersChanged(false);
    adminApi.getUnusedLabels(query).then((response) => {
      if (currentRequest !== requestId.current) return;
      if (!response || !Array.isArray(response.items) || !response.pagination || !response.summary) {
        throw new Error('The unused labels report returned an incomplete response. Please refresh to try again.');
      }
      setData(response);
    }).catch((err) => {
      if (currentRequest !== requestId.current) return;
      setError(err.response?.data?.message || err.message || 'Unable to load unused labels. Please try again.');
    }).finally(() => {
      if (currentRequest === requestId.current) setLoading(false);
    });
    return () => { requestId.current += 1; };
  }, [query]);

  const updateFilter = (name, value) => {
    // Editing filters invalidates any previous or in-flight report without issuing a request.
    requestId.current += 1;
    setDraft((previous) => ({ ...previous, [name]: value }));
    setData(null);
    setError('');
    setLoading(false);
    setFiltersChanged(true);
    setValidationError('');
  };

  const submitFilters = (event) => {
    event.preventDefault();
    const minAgeDays = Number(draft.minAgeDays);
    const lookbackDays = Number(draft.lookbackDays);
    if (draft.minAgeDays === '' || draft.lookbackDays === '' ||
        !Number.isInteger(minAgeDays) || minAgeDays < 0 || minAgeDays > 3650 ||
        !Number.isInteger(lookbackDays) || lookbackDays < 1 || lookbackDays > 3650 || lookbackDays <= minAgeDays) {
      setValidationError('Enter whole days: minimum age from 0 to 3650 and lookback from 1 to 3650. Lookback must be greater than minimum age.');
      return;
    }
    setValidationError('');
    setQuery({ minAgeDays, lookbackDays, search: draft.search.trim(), carrier: draft.carrier, service: draft.service.trim(), page: 1, pageSize: 25 });
  };

  const likelyUnused = data?.items.filter((item) => item.classification === 'LIKELY_UNUSED') || [];
  const needsReview = data?.items.filter((item) => !['LIKELY_UNUSED', 'USED', 'EXCLUDED'].includes(item.classification)) || [];
  const otherChecked = data?.items.filter((item) => ['USED', 'EXCLUDED'].includes(item.classification)) || [];
  const pagination = data?.pagination;
  const summary = data?.summary;

  return (
    <div>
      <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Unused Labels</h1>
          <p className="mt-1 text-sm text-gray-600">Review purchased labels using their latest available carrier tracking status.</p>
        </div>
        <button type="button" onClick={() => setQuery((previous) => ({ ...previous }))} disabled={loading || filtersChanged}
          className={`${BUTTON_CLASS} inline-flex items-center justify-center bg-white`}>
          <ArrowPathIcon aria-hidden="true" className={`mr-2 h-5 w-5 ${loading ? 'animate-spin' : ''}`} /> Refresh batch
        </button>
      </div>

      <div className="mb-5 rounded-md border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900">
        <strong>Likely unused</strong> means the label was created successfully and the carrier reports pre-transit.
        Tracking may be delayed; this does not prove a label is unused or eligible for a refund.
        Missing tracking and failed checks appear separately under Needs review.
      </div>

      <form onSubmit={submitFilters} className="rounded-lg bg-white p-4 shadow" aria-label="Unused label filters">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <label className="text-sm font-medium text-gray-700">
            Minimum age (days)
            <input type="number" min="0" max="3650" step="1" required value={draft.minAgeDays}
              onChange={(event) => updateFilter('minAgeDays', event.target.value)} className={FIELD_CLASS} />
          </label>
          <label className="text-sm font-medium text-gray-700">
            Lookback (days)
            <input type="number" min="1" max="3650" step="1" required value={draft.lookbackDays}
              onChange={(event) => updateFilter('lookbackDays', event.target.value)} className={FIELD_CLASS} />
          </label>
          <label className="text-sm font-medium text-gray-700">
            Carrier
            <select value={draft.carrier} onChange={(event) => updateFilter('carrier', event.target.value)} className={FIELD_CLASS}>
              <option value="">All carriers</option>
              {['USPS', 'UPS', 'FedEx', 'DHL'].map((carrier) => <option key={carrier} value={carrier}>{carrier}</option>)}
            </select>
          </label>
          <label className="text-sm font-medium text-gray-700">
            Service
            <input type="search" maxLength="100" value={draft.service} placeholder="e.g. 2nd Day Air, Ground"
              onChange={(event) => updateFilter('service', event.target.value)} className={FIELD_CLASS} />
            <span className="mt-1 block text-xs font-normal text-gray-500">
              Carrier is the company (UPS); service is the product (2nd Day Air).
            </span>
          </label>
          <label className="text-sm font-medium text-gray-700">
            Search
            <input type="search" maxLength="200" value={draft.search} placeholder="Order, email or tracking"
              onChange={(event) => updateFilter('search', event.target.value)} className={FIELD_CLASS} />
          </label>
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <button type="submit" disabled={loading} className="min-h-[44px] rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2">
            Apply filters
          </button>
          <p className="text-xs text-gray-500">Up to 25 candidates are checked per batch. Usage counts describe this batch; total candidates cover the selected window.</p>
        </div>
        {validationError && <p role="alert" className="mt-3 text-sm text-red-700">{validationError}</p>}
      </form>

      <div className="mt-5" aria-live="polite" aria-atomic="true">
        {loading && <p role="status" className="rounded-md bg-white p-6 text-center text-gray-600">Checking carrier status for this batch…</p>}
        {filtersChanged && <p className="text-sm text-gray-600">Filters changed. Apply filters to check matching labels.</p>}
      </div>
      {error && <div role="alert" className="mt-5 rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-800"><strong>Report unavailable.</strong> {error}</div>}

      {data && (
        <>
          <dl className="mt-5 grid grid-cols-2 gap-3 lg:grid-cols-4">
            {[
              ['Total candidates', pagination.totalCandidates],
              ['Checked in this batch', summary.scanned],
              ['Likely unused in batch', summary.likelyUnused],
              ['Needs review in batch', (summary.trackingUnknown || 0) + (summary.checkFailed || 0)],
            ].map(([title, value]) => (
              <div key={title} className="rounded-lg bg-white p-4 shadow">
                <dt className="text-xs font-medium text-gray-500">{title}</dt>
                <dd className="mt-1 text-2xl font-bold text-gray-900">{value ?? 0}</dd>
              </div>
            ))}
          </dl>
          <p className="mt-3 text-xs text-gray-500">
            Purchases between {query.minAgeDays} and {query.lookbackDays} days old. Report generated {formatDate(data.generatedAt)}.
          </p>

          {pagination.totalCandidates === 0 ? (
            <p className="mt-5 rounded-lg bg-white p-8 text-center text-gray-600">No purchased label candidates match these filters.</p>
          ) : (
            <>
              <LabelGroup title="Likely unused" description="Successful labels reported as pre-transit by the carrier."
                items={likelyUnused} emptyMessage="No likely unused labels in this batch. Other batches may contain matching labels."
                selectable selectedIds={selectedIds} onToggle={toggleSelected} />
              <VoidRefundPanel
                selectedIds={selectedIds}
                onClear={() => setSelectedIds([])}
                onCompleted={() => { setSelectedIds([]); setQuery((q) => ({ ...q })); }}
              />
              <LabelGroup title="Needs review" description="Tracking is missing, unrecognized or could not be checked; usage is unconfirmed."
                items={needsReview} emptyMessage="No unknown tracking statuses or failed checks in this batch." />
              {otherChecked.length > 0 && (
                <details className="mt-6 rounded-lg border border-gray-200 p-4">
                  <summary className="cursor-pointer text-sm font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500">
                    Other checked labels ({otherChecked.length}) — carrier activity or excluded
                  </summary>
                  <ul className="mt-3 space-y-3">{otherChecked.map((item) => <LabelCard key={item.id || item.orderId} item={item} />)}</ul>
                </details>
              )}
            </>
          )}

          <nav aria-label="Candidate batch pagination" className="mt-6 flex flex-wrap items-center justify-between gap-3 border-t border-gray-200 pt-4">
            <p className="text-sm text-gray-600">
              {pagination.totalCandidates > 0 ? `Batch ${pagination.page} of ${pagination.totalPages}` : 'No candidate batches'}
              {' · '}{pagination.totalCandidates} total candidates
            </p>
            <div className="flex gap-2">
              <button type="button" disabled={loading || pagination.page <= 1} className={BUTTON_CLASS}
                onClick={() => setQuery((previous) => ({ ...previous, page: pagination.page - 1 }))}>Previous batch</button>
              <button type="button" disabled={loading || pagination.page >= pagination.totalPages} className={BUTTON_CLASS}
                onClick={() => setQuery((previous) => ({ ...previous, page: pagination.page + 1 }))}>Next batch</button>
            </div>
          </nav>
        </>
      )}
    </div>
  );
};

export default UnusedLabels;
